/**
 * reviewMoneyModel — what the money on a flagged item actually MEANS (BON-381).
 *
 * The money card used to be five figures and a subtraction, leaving the reader to
 * work out the story: order, authorized, captured, maybe refunded, and an
 * "Unreconciled" total with no statement of which way it went or why it mattered.
 * On a page whose entire job is "tell me what went wrong with this payment", that
 * is the one thing it must not make the operator do in their head.
 *
 * WHAT RECONCILES AGAINST WHAT is the substantive fix here, and it was wrong.
 * "Unreconciled" compared Stripe's net against the ORDER TOTAL — so a booking
 * whose extra seat had been refunded showed "$70.40 short" forever, even though
 * our own cash record and Stripe agreed to the penny. ADR-0019 is explicit that
 * `amountPaidCents` is the cash ledger and a refund REDUCES it, so on a booking
 * the figure that must agree with Stripe is what we recorded as paid. The order
 * total is what was OWED, which is a different question.
 *
 * A checkout has no `amountPaidCents` — there is no booking to hold one — so
 * there the order total is the only base available, and it is the right one: money
 * taken against an order that produced nothing.
 *
 * Two rules behind the sentence:
 *
 *   1. When the money does not add up, say what is missing and in which
 *      direction — short-captured and over-captured are different problems with
 *      different remedies, and "$13.40" alone does not distinguish them.
 *   2. When the money DOES add up, say so explicitly rather than leaving a green
 *      $0.00 to be inferred. A silent zero is indistinguishable from a card that
 *      failed to load, and on a flagged item the operator still needs to know the
 *      flag was not about money at all.
 *
 * Kept framework-free and separate from `reviewQueueModel` so every sentence is
 * directly unit-testable, and so neither file crosses the 300-line cap.
 */
import { formatMoney, isGiftPassPurchase } from './reviewQueueModel';
import type { ReviewDetail } from './reviewTypes';

export interface MoneyExplanation {
  /** The whole story, in one to three sentences. */
  text: string;
  /** False when something still needs an operator. */
  settled: boolean;
}

/** What Stripe is actually holding now: captured, less anything refunded. */
export function netHeldCents(capturedCents: number, refundedCents: number): number {
  return capturedCents - refundedCents;
}

type MoneyDetail = Pick<
  ReviewDetail,
  | 'kind'
  | 'workshop'
  | 'totalCents'
  | 'currency'
  | 'amountPaidCents'
  | 'storeCreditRefundedCents'
  | 'stripe'
  | 'stripeUnavailable'
  | 'paymentIntentId'
>;

export interface ReconcileBase {
  cents: number;
  /** How the sentence refers to it. */
  label: string;
}

/**
 * The figure Stripe's net cash SHOULD equal.
 *
 * Our recorded cash when we have it (a booking), else the order total (a
 * checkout, which has no booking to record cash against).
 */
export function reconcileBase(
  detail: Pick<MoneyDetail, 'amountPaidCents' | 'totalCents' | 'storeCreditRefundedCents'>,
): ReconcileBase {
  if (detail.amountPaidCents === null) {
    return { cents: detail.totalCents, label: 'order' };
  }
  // Store credit has to be added back. A gift-card refund zeroes
  // `amountPaidCents` but moves NO money at Stripe, so comparing Stripe's net
  // against the reduced figure alone reports the full original charge as an
  // unexplained over-capture — on the one surface this refund path routes an
  // operator to. The cash left the booking; it stayed in the business.
  const credit = detail.storeCreditRefundedCents;
  return credit > 0
    ? { cents: detail.amountPaidCents + credit, label: 'we recorded as paid plus store credit' }
    : { cents: detail.amountPaidCents, label: 'we recorded as paid' };
}

/** Signed gap: positive means Stripe holds LESS than our books say. */
export function reconcileGapCents(detail: MoneyDetail): number {
  if (!detail.stripe) return reconcileBase(detail).cents;
  return (
    reconcileBase(detail).cents -
    netHeldCents(detail.stripe.capturedCents, detail.stripe.refundedCents)
  );
}

/**
 * The money sentence, or null when there is no Stripe snapshot to reason about
 * (the card already has honest copy for "no intent" and "Stripe unreachable", and
 * inventing a reconciliation story without Stripe's numbers would be a guess).
 */
export function reconciliationSummary(detail: MoneyDetail): MoneyExplanation | null {
  const { stripe, currency, totalCents } = detail;
  if (!stripe) return null;

  const base = reconcileBase(detail);
  const net = netHeldCents(stripe.capturedCents, stripe.refundedCents);
  const gap = base.cents - net;

  const held = formatMoney(net, currency);
  const expected = formatMoney(base.cents, currency);
  const off = formatMoney(Math.abs(gap), currency);

  const core = coreSentence({
    gap,
    held,
    expected,
    off,
    baseLabel: base.label,
    authorized: formatMoney(stripe.amountCents, currency),
    capturedCents: stripe.capturedCents,
    authorizedCents: stripe.amountCents,
  });

  const shortfall = orderShortfallNote(detail, base);
  const orphan = orphanNote(detail, net, held);

  return {
    text: [core.text, shortfall.text, orphan].filter(Boolean).join(' '),
    // Three independent things have to be true: Stripe agrees with our books, the
    // books actually cover the order, and (for a checkout) the money is not
    // orphaned. The first alone is not "settled" — a booking whose books and
    // Stripe agree at HALF the order still leaves the order uncovered.
    settled: core.settled && shortfall.settled && orphan === '',
  };

  /**
   * Our books cover less than the order. Two different causes, and attributing
   * the wrong one is worse than saying nothing:
   *
   *   - Stripe shows a REFUND → deliberate, the books already know, settled.
   *   - Stripe shows none → the order was never fully paid, which is a live
   *     problem and the usual reason a reconciliation mismatch is flagged.
   *
   * The first version claimed "refunded" for both, so a short-captured booking
   * was told a refund had happened that never did.
   */
  function orderShortfallNote(d: MoneyDetail, b: ReconcileBase): MoneyExplanation {
    if (d.amountPaidCents === null || b.cents >= totalCents) {
      return { text: '', settled: true };
    }
    const missing = formatMoney(totalCents - b.cents, currency);
    const order = formatMoney(totalCents, currency);
    // A store-credit refund is a refund too, even though Stripe shows none —
    // keying this solely off `stripe.refundedCents` told the operator that
    // "no refund explains the difference" about a booking we had just refunded.
    if ((stripe && stripe.refundedCents > 0) || d.storeCreditRefundedCents > 0) {
      return {
        text: `${missing} of the ${order} order was refunded, so the books and Stripe both show the reduced figure.`,
        settled: true,
      };
    }
    return {
      text: `But the order was ${order}, so ${missing} of it was never paid — no refund explains the difference.`,
      settled: false,
    };
  }

  /** A CHECKOUT holding money is why it is here, reconciled or not. */
  function orphanNote(d: MoneyDetail, netCents: number, heldText: string): string {
    if (d.kind !== 'checkout' || netCents <= 0) return '';
    const missing = isGiftPassPurchase(d)
      ? 'No gift passes were ever issued'
      : 'No booking was ever created';
    return `${missing}, so this ${heldText} is money taken with nothing to show for it.`;
  }
}

interface Core {
  gap: number;
  held: string;
  expected: string;
  off: string;
  baseLabel: string;
  authorized: string;
  capturedCents: number;
  authorizedCents: number;
}

function coreSentence(f: Core): MoneyExplanation {
  // Authorized but never captured: no money has moved, which is a different
  // situation from a shortfall and must not be described as one.
  if (f.capturedCents === 0) {
    return f.authorizedCents > 0
      ? {
          text: `Stripe authorized ${f.authorized} but captured nothing, so no money has moved yet.`,
          settled: false,
        }
      : { text: 'Stripe has neither authorized nor captured anything.', settled: false };
  }

  if (f.gap > 0) {
    return {
      text: `Stripe holds ${f.held} against the ${f.expected} ${f.baseLabel} — ${f.off} short.`,
      settled: false,
    };
  }

  if (f.gap < 0) {
    return {
      text: `Stripe holds ${f.held} against the ${f.expected} ${f.baseLabel} — ${f.off} MORE than our books show.`,
      settled: false,
    };
  }

  return {
    text: `Stripe holds ${f.held}, matching the ${f.expected} ${f.baseLabel} exactly.`,
    settled: true,
  };
}
